<script lang="ts" setup>
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Password from 'primevue/password';
import Message from 'primevue/message';
import IftaLabel from 'primevue/iftalabel';
import Button from 'primevue/button';
import { FormField, Form } from "@primevue/forms";
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import { watch, ref, onMounted, computed, useTemplateRef, nextTick } from "vue";
import { useApiKeysStore } from "@/stores/apiKeysStore";
import { i18n } from "@/i18n";
import type { FormInstance, FormSubmitEvent } from "@primevue/forms"
import type { ApiKeyType } from "@/services/api-keys-v2-service/types";
import { useMyToast } from '@/composable/useMyToast';
import { useAuthStore } from '@/stores/authStore';
import { isNull, isUndefined } from 'lodash';

const { t } = i18n.global
const { addToast } = useMyToast()
const apiKeysStore = useApiKeysStore()
const authStore = useAuthStore()
const apiForm = useTemplateRef<FormInstance>("apiForm")

export type initValueType = Record<string, string | number | null | object>
interface apiKeyProp {
  show?: boolean
  label?: string
}
export interface availableProps {
  id: string,
  profileNum?: number
  apiKey?: apiKeyProp,
  secret?: apiKeyProp,
  password?: apiKeyProp,
  fingerPrint?: apiKeyProp,
  proxy?: apiKeyProp,
  userAgent?: apiKeyProp,
  webToken?: apiKeyProp,

  // 1Inch
  evmAddress?: apiKeyProp
  solAddress?: apiKeyProp

  // BingX
  deviceId?: apiKeyProp,
  platformId?: apiKeyProp,
  appVersion?: apiKeyProp,
  antiDeviceId?: apiKeyProp,
  appId?: apiKeyProp,
  deviceBrand?: apiKeyProp,
  userId?: apiKeyProp,

  privateKey?: apiKeyProp,

  keyType?: ApiKeyType
  initValues?: initValueType
}
const props = defineProps<availableProps>();
const proxyRegex = /^(?:(https?|socks5h?):\/\/)(?:([^:@\s]+):([^@\s]+)@)?(\d{1,3}(?:\.\d{1,3}){3}):(\d{1,5})\/?$/;
const cleanableApiKey = computed(() => Boolean(props.initValues?.apiKey) || Boolean(props.initValues?.webToken))

const buildResolver = () => {
  const zodObject: Record<string, object> = {}
  if (props.apiKey?.show) {
    zodObject['apiKey'] = z.string().min(1, { message: 'modal.apiKeys.errors.required' })
  }
  if (props.secret?.show) {
    zodObject['secret'] = z.string().min(1, { message: 'modal.apiKeys.errors.required' })
  }
  if (props.password?.show) {
    zodObject['password'] = z.string().min(1, { message: 'modal.apiKeys.errors.required' })
  }
  if (props.fingerPrint?.show) {
    zodObject['fingerPrint'] = z.string().min(1, { message: 'modal.apiKeys.errors.required' })
  }
  if (props.proxy?.show ?? true) {
    zodObject['proxy'] = z.union([
      z.string().regex(proxyRegex, { message: "modal.apiKeys.errors.proxyError" }),
      z.literal(''),
      z.null(),
      z.undefined()
    ]).optional();
  }
  if (props.userAgent?.show) {
    zodObject['userAgent'] = z.string().optional().nullable()
  }
  if (props.webToken?.show) {
    zodObject['webToken'] = z.string().min(1, { message: 'modal.apiKeys.errors.required' }).optional().nullable()
  }

  for (const x of extraFields) {
    const prop = (props as Record<string, any>)[x.name] // eslint-disable-line
    if (prop?.show ?? false) {
      zodObject[x.name] = z.string().optional().nullable()
    }
  }

  return zodResolver(
    z.object(zodObject)
  );
}

interface SelectValue {
  title: string
  value: number | null
}

const dupProfile = ref<number | null>(null)
const profiles = ref<SelectValue[]>([
  {
    title: t("modal.apiKeys.selectDuplicate"),
    value: null
  }
])

const putting = ref(false)
const onFormSubmit = async (r: FormSubmitEvent) => {
  const valid = r.valid
  const values = r.values
  if (valid && props.profileNum !== null && props.profileNum !== undefined) {
    putting.value = true
    await apiKeysStore.saveKey(props.id, props.profileNum, props.keyType ?? null, values as Record<string, string | null | number>)
    if (apiKeysStore.error.save) {
      const data = apiKeysStore.error.save.message
      const type = data?.detail?.[0]?.type
      if (type) {
        addToast({ severity: 'error', message: t(`modal.apiKeys.errors.api.${type}`) });
      } else {
        addToast({ severity: 'error', message: t(`unexceptedError`) });
      }
    } else {
      addToast({ severity: 'success', message: t(`modal.apiKeys.saved`) });
    }
    // синхронизируем данные форм
    await apiKeysStore.fetchKeys(true)
    putting.value = false
  }
};
const resolver = ref()

const extraFields = [
  {
    name: "antiDeviceId",
  },
  {
    name: "appVersion",
  },
  {
    name: "appId",
  },
  {
    name: "deviceBrand",
  },
  {
    name: "deviceId",
  },
  {
    name: "platformId",
  },
  {
    name: "userId",
  },
  {
    name: "solAddress"
  },
  {
    name: "evmAddress"
  },
]

const shouldShowField = (key: string): boolean => {
  // Check if key exists and is an object with 'show' property
  const prop = (props as Record<string, any>)[key] // eslint-disable-line
  return prop?.show ?? false
}

const clean = async () => {
  putting.value = true
  await apiKeysStore.deleteKey(props.id, props.profileNum ?? 0, props.keyType ?? null)
  if (apiKeysStore.error.delete) {
    const data = apiKeysStore.error.delete.message
    const type = data?.detail?.[0]?.type
    if (type) {
      addToast({ severity: 'error', message: t(`modal.apiKeys.errors.api.${type}`) });
    } else {
      addToast({ severity: 'error', message: t(`unexceptedError`) });
    }
  } else {
    addToast({ severity: 'success', message: t(`modal.apiKeys.saved`) });
  }
  // синхронизируем данные формы
  await apiKeysStore.fetchKeys(true)
  await nextTick()
  apiForm.value?.reset()
  putting.value = false
}

const activeProfiles = computed(() => props.keyType == "cex_cex" ? authStore.profiles.cex_cex : authStore.profiles.dex_cex)
const disabled = computed(() => apiKeysStore.loading || putting.value || dupProfile.value !== null)
const btnLoading = computed(() => apiKeysStore.loading || putting.value)

onMounted(() => {
  resolver.value = buildResolver()
  for (const k of activeProfiles.value) {
    if (k == props.profileNum) {
      continue
    }
    profiles.value.push({
      title: t("modal.apiKeys.selectDuplicateX", { profile: k + 1 }),
      value: k
    })
  }
  dupProfile.value = (props.initValues?.["duplicate"] ?? null) as number | null

  watch(dupProfile, async v => {
    if (isUndefined(props.profileNum)) {
      return
    }
    if (!isNull(v)) {
      addToast({ severity: 'success', message: t(`modal.apiKeys.profileDuplicated`) });
    }
    await apiKeysStore.updateProfileDuplicate(
      props.id,
      props.profileNum,
      props.keyType ?? 'cex_cex',
      v
    )
    // синхронизируем данные формы
    await apiKeysStore.fetchKeys(true)
  })

})
</script>

<template>
  <Form :resolver ref="apiForm" @submit="onFormSubmit" class="grid grid-cols-1 gap-2 items-center w-full"
    autocomplete="off" :initial-values="initValues">

    <FormField v-if="props.apiKey?.show ?? false" v-slot="$field" name="apiKey">
      <IftaLabel>
        <InputText type="text" fluid :placeholder="$t(props.apiKey?.label ?? 'modal.apiKeys.apiKey')" :loading="true"
          :disabled="disabled" />
        <label>{{ $t(props.apiKey?.label ?? 'modal.apiKeys.apiKey') }}</label>
      </IftaLabel>
      <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message) }}
      </Message>
    </FormField>

    <FormField v-if="props.secret?.show ?? false" v-slot="$field" name="secret">
      <IftaLabel>
        <Password :feedback="false" type="text" :placeholder="$t(props.secret?.label ?? 'modal.apiKeys.secret')"
          toggleMask fluid :disabled="disabled" />
        <label>{{ $t(props.secret?.label ?? 'modal.apiKeys.secret') }}</label>
      </IftaLabel>
      <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message) }}
      </Message>
    </FormField>

    <FormField v-if="props.password?.show ?? false" v-slot="$field" name="password">
      <IftaLabel>
        <Password :feedback="false" type="text" :placeholder="$t(props.password?.label ?? 'modal.apiKeys.password')"
          toggleMask fluid :disabled="disabled" />
        <label>{{ $t(props.password?.label ?? 'modal.apiKeys.password') }}</label>
      </IftaLabel>
      <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message) }}
      </Message>
    </FormField>

    <FormField v-if="props.webToken?.show ?? false" v-slot="$field" name="webToken">
      <IftaLabel>
        <Password :feedback="false" type="text" :placeholder="$t('modal.apiKeys.webToken')" toggleMask fluid
          :disabled="disabled" />
        <label>{{ $t('modal.apiKeys.webToken') }}</label>
      </IftaLabel>
      <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message) }}
      </Message>
    </FormField>

    <FormField v-if="props.fingerPrint?.show ?? false" v-slot="$field" name="fingerPrint">
      <IftaLabel>
        <Password :feedback="false" type="text" :placeholder="$t('modal.apiKeys.fingerPrint')" toggleMask fluid
          :disabled="disabled" />
        <label>{{ $t('modal.apiKeys.fingerPrint') }}</label>
      </IftaLabel>
      <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message) }}
      </Message>
    </FormField>

    <FormField v-if="props.userAgent?.show ?? false" v-slot="$field" name="userAgent">
      <IftaLabel>
        <InputText type="text" :placeholder="$t('modal.apiKeys.userAgent')" fluid :disabled="disabled" />
        <label>{{ $t('modal.apiKeys.userAgent') }}</label>
      </IftaLabel>
      <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message) }}
      </Message>
    </FormField>

    <template v-for="field in extraFields" :key="field.name">
      <!-- Special fields for BingX -->
      <FormField v-show="shouldShowField(field.name)" v-slot="$field" :name="field.name">
        <IftaLabel>
          <InputText :feedback="false" type="text" :placeholder="$t(`modal.apiKeys.${field.name}`)" toggleMask fluid
            :disabled="disabled" />
          <label>{{ $t(`modal.apiKeys.${field.name}`) }}</label>
        </IftaLabel>
        <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message) }}
        </Message>
      </FormField>
    </template>

    <FormField v-if="props.proxy?.show ?? true" v-slot="$field" name="proxy">
      <IftaLabel>
        <InputText type="text" :placeholder="$t('modal.apiKeys.proxy')" fluid :disabled="disabled" />
        <label>{{ $t('modal.apiKeys.proxy') }}</label>
      </IftaLabel>
      <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message) }}
      </Message>
    </FormField>

    <div class="flex gap-1">
      <Button :disabled="dupProfile !== null || btnLoading" type="submit" severity="secondary" icon="pi pi-save"
        :label="$t('save')" :loading="btnLoading" fluid />
      <Button :disabled="dupProfile !== null || btnLoading" type="button" @click="clean" v-if="cleanableApiKey"
        severity="secondary" icon="pi pi-trash" :loading="btnLoading" fluid />
    </div>

    <Select v-model="dupProfile" :options="profiles" option-label="title" option-value="value"
      :placeholder="$t('modal.apiKeys.selectDuplicate')">
    </Select>

  </Form>
</template>
